import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Calendar, Globe, Mail, Plus, X, Download, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import { BRAND } from "../lib/brand";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from "../lib/brand";

export default function QuickActions() {
  const [open, setOpen] = useState(false);
  const [newsletterOpen, setNewsletterOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/newsletter/subscribe`, { email });
      toast.success(data.message || "Iscrizione completata!");
      setEmail("");
      setNewsletterOpen(false);
    } catch (err) {
      toast.error("Errore. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const actions = [
    { icon: MessageCircle, label: "WhatsApp", href: BRAND.whatsappUrl, testId: "quick-whatsapp", color: "bg-[#25D366]", external: true },
    { icon: Calendar, label: "Prenota", to: "/prenota", testId: "quick-booking", color: "bg-[#FF2768]" },
    { icon: Download, label: "Guida gratuita", to: "/guida-gratuita", testId: "quick-guida", color: "bg-[#81C3C9]" },
    { icon: Instagram, label: "Instagram", href: BRAND.instagram, testId: "quick-instagram", color: "bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF]", external: true },
    { icon: Mail, label: "Newsletter", onClick: () => setNewsletterOpen(true), testId: "quick-newsletter", color: "bg-[#3D2140]" },
  ];

  return (
    <>
      <div className="fixed bottom-6 right-6 z-40 flex flex-col-reverse items-end gap-3">
        <motion.button
          data-testid="quick-actions-toggle"
          onClick={() => setOpen(!open)}
          className="h-14 w-14 rounded-full bg-[#FF2768] text-white shadow-2xl flex items-center justify-center border-2 border-white"
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.95 }}
          animate={{ rotate: open ? 45 : 0 }}
        >
          <Plus size={22} />
        </motion.button>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col gap-3"
            >
              {actions.map((a, idx) => {
                const Icon = a.icon;
                const content = (
                  <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 30 }}
                    transition={{ delay: idx * 0.06 }}
                    className="group flex items-center gap-3"
                  >
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white text-[#3D2140] text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg whitespace-nowrap">
                      {a.label}
                    </span>
                    <span className={`h-12 w-12 rounded-full ${a.color} text-white flex items-center justify-center shadow-xl border-2 border-white transition-transform hover:scale-110`}>
                      <Icon size={18} />
                    </span>
                  </motion.div>
                );
                if (a.href) {
                  return (
                    <a key={a.label} href={a.href} target={a.external ? "_blank" : undefined} rel="noopener noreferrer" data-testid={a.testId}>
                      {content}
                    </a>
                  );
                }
                if (a.to) {
                  return <Link key={a.label} to={a.to} data-testid={a.testId}>{content}</Link>;
                }
                return (
                  <button key={a.label} onClick={a.onClick} data-testid={a.testId}>
                    {content}
                  </button>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {newsletterOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-[#3D2140]/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setNewsletterOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl p-8 md:p-10 max-w-md w-full relative border-t-8 border-[#FF2768]"
              data-testid="newsletter-modal"
            >
              <button
                onClick={() => setNewsletterOpen(false)}
                className="absolute top-4 right-4 h-8 w-8 rounded-full bg-[#FAF8F5] flex items-center justify-center"
                data-testid="newsletter-close"
              >
                <X size={16} />
              </button>
              <h3 className="font-display text-4xl md:text-5xl text-[#3D2140] mb-3">Resta in contatto</h3>
              <p className="text-[#3D2140]/70 mb-6 leading-relaxed">
                Riflessioni, strumenti e appuntamenti per la vita familiare. Con cura, mai troppe email.
              </p>
              <form onSubmit={subscribe} className="flex flex-col gap-3">
                <input
                  type="email"
                  required
                  placeholder="La tua email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="newsletter-email-input"
                  className="w-full px-5 py-4 rounded-full border-2 border-[#81C3C9]/40 focus:border-[#FF2768] transition-colors bg-[#FAF8F5]"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="newsletter-submit"
                  className="btn-tv-primary justify-center"
                >
                  {loading ? "Un momento…" : "Iscrivimi alla newsletter"}
                </button>
              </form>
              <p className="text-xs text-[#3D2140]/50 mt-4">
                Rispetto la tua privacy. Nessuno spam, mai.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
